import {
  formatPhone,
  jobAddress,
  primaryPhone,
  threadPreview,
  toE164,
  type Contact,
  type Job,
  type TextLine,
  type Thread,
  type ThreadText,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { fmtDateTime } from "../../lib/format";
import { navigate } from "../../lib/hooks";
import { jobLink } from "../../lib/jobLink";
import type { CallRow } from "../../lib/queries";
import { TextsPanel } from "../job/TextsPanel";
import type { AppData } from "../shell/Shell";
import { CallButton } from "./CallButton";
import { ContactHits, findContacts } from "./ContactHits";
import { Recents } from "./Recents";

/**
 * The phone, open (ADR 0321).
 *
 * Jonathon, 2026-10-24: "i want it to mirror iphone as much as possible for
 * simplicity sake", and 2026-10-27, choosing the bubble: "think iphone in
 * simplicity and visibilty." So it is drawn the way a phone draws it, in a
 * panel the size of one: a line picker and a customer search at the top, the
 * three tabs a phone has along the bottom. Messages is every conversation
 * newest first with the last thing said under the name, and a tap opens it
 * in place with a way back; Recents is every call in or out with the missed
 * ones in red; Voicemail is the messages left, with a play button and the
 * words written out.
 *
 * Nothing here is worked out on the screen. Which texts make a conversation
 * and what counts as unanswered is `buildThreads`; what a call is called is
 * `recentsLabel`; what the badge counts is `phoneBadge`; all in
 * packages/core. A conversation is the job's own Texts card, so answering
 * here is answering there. The bubble that opens this, and the state it
 * keeps, is `PhoneBubble`.
 */
export type PhoneTab = "messages" | "recents" | "voicemail";

export const PHONE_TABS: readonly [PhoneTab, string][] = [
  ["messages", "Messages"],
  ["recents", "Recents"],
  ["voicemail", "Voicemail"],
];

/**
 * A conversation on screen: one of the threads, or a customer picked from
 * the search who has no texts yet and so no thread.
 */
interface Opened {
  key: string;
  contact_id: string | null;
  phone: string | null;
  job_id: string | null;
  latest: ThreadText | null;
}

export function PhonePanel({
  data,
  threads,
  textsLoading,
  textsError,
  calls,
  callsLoading,
  callsError,
  onReread,
  tab,
  onTab,
  lines,
  line,
  callLine,
  onLine,
  onForgetLine,
  onMinimize,
  onLeave,
}: {
  data: AppData;
  threads: Thread[];
  /** The conversations have not been read yet, as against there being none. */
  textsLoading: boolean;
  textsError: string | null;
  calls: CallRow[];
  /** The calls have not been read yet, as against there being none. */
  callsLoading: boolean;
  callsError: string | null;
  /** Fetch the calls again, after a transcript was asked for. */
  onReread: () => Promise<void>;
  tab: PhoneTab;
  onTab: (tab: PhoneTab) => void;
  /** Our lines, the rep's own first. */
  lines: TextLine[];
  /** The line texts leave from, as picked here. */
  line: string | null;
  /** The line calls leave from: the one picked, else the rep's own, else
   * none, so twilio-call uses the Roof Board line. */
  callLine: string | null;
  onLine: (line: string) => void;
  /** Back to the rep's own line, when a conversation is closed (ADR 0321). */
  onForgetLine: () => void;
  onMinimize: () => void;
  /** Called before the panel sends the rep somewhere else in the app. */
  onLeave: () => void;
}) {
  const [search, setSearch] = useState("");
  const [picked, setPickedState] = useState<Opened | null>(null);

  // Closing a conversation, or leaving it for another, puts the phone back
  // on the rep's own line (ADR 0321).
  function setPicked(next: Opened | null) {
    if (picked && picked.key !== next?.key) onForgetLine();
    setPickedState(next);
  }

  const names = useMemo(
    () => new Map(data.contacts.map((c) => [c.id, c.full_name])),
    [data.contacts],
  );
  const jobById = useMemo(
    () => new Map(data.jobs.map((job) => [job.id, job])),
    [data.jobs],
  );

  const nameOf = (opened: Opened) =>
    (opened.contact_id && names.get(opened.contact_id)) ||
    formatPhone(opened.phone) ||
    "Unknown number";

  // The job a conversation opens on: the one its newest text named, else the
  // customer's newest job. A stranger has neither.
  const jobOf = (opened: Opened): Job | null => {
    const named = opened.job_id ? jobById.get(opened.job_id) : undefined;
    if (named) return named;
    if (!opened.contact_id) return null;
    return (
      data.jobs
        .filter((job) => job.contact_id === opened.contact_id)
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0] ?? null
    );
  };

  // The customer picked from the search: their conversation where there is
  // one, else a fresh one, so a customer nobody has texted yet can be.
  function openContact(contact: Contact) {
    const key = `contact:${contact.id}`;
    const have = threads.find((t) => t.key === key);
    setPicked(
      have ?? {
        key,
        contact_id: contact.id,
        phone: toE164(primaryPhone(contact))?.replace(/^\+1/, "") ?? null,
        job_id: null,
        latest: null,
      },
    );
    setSearch("");
    onTab("messages");
  }

  const hits = useMemo(
    () => (search.trim() ? findContacts(data.contacts, search) : []),
    [data.contacts, search],
  );

  const current = picked
    ? (threads.find((t) => t.key === picked.key) ?? picked)
    : null;

  return (
    <section
      className="phone-panel"
      role="dialog"
      aria-label="Phone"
      onKeyDown={(e) => {
        if (e.key === "Escape") onMinimize();
      }}
    >
      <div className="phone-head">
        <h2>Phone</h2>
        <button
          type="button"
          className="phone-min"
          aria-label="Minimize the phone"
          title="Minimize"
          onClick={onMinimize}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path
              d="M5 12h14"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {lines.length > 0 ? (
        <label className="phone-line">
          <span>From</span>
          <select
            aria-label="Your line"
            value={line ?? lines[0]?.number ?? ""}
            onChange={(e) => onLine(e.target.value)}
          >
            {lines.map((option) => (
              <option key={option.number} value={option.number}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <label className="phone-find">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <circle
            cx="11"
            cy="11"
            r="7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="M21 21l-4.3-4.3"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="search"
          aria-label="Find a customer"
          placeholder="Who are you texting or calling?"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>

      <div className="phone-body">
        {search.trim() ? (
          <ContactHits
            hits={hits}
            jobs={data.jobs}
            onPick={openContact}
            typed={search}
          />
        ) : tab === "messages" ? (
          current ? (
            <Conversation
              key={current.key}
              opened={current}
              name={nameOf(current)}
              job={jobOf(current)}
              line={line}
              callLine={callLine}
              onLine={onLine}
              onBack={() => setPicked(null)}
              onLeave={onLeave}
            />
          ) : (
            <Conversations
              threads={threads}
              loading={textsLoading}
              error={textsError}
              nameOf={nameOf}
              onPick={setPicked}
            />
          )
        ) : (
          <Recents
            calls={calls}
            loading={callsLoading}
            error={callsError}
            jobById={jobById}
            voicemailOnly={tab === "voicemail"}
            line={callLine}
            onReread={onReread}
            onLeave={onLeave}
          />
        )}
      </div>

      <div className="phone-tabs" role="tablist" aria-label="Phone">
        {PHONE_TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key && !search.trim()}
            className={tab === key ? "on" : undefined}
            onClick={() => {
              setSearch("");
              if (key === "messages" && tab === "messages") setPicked(null);
              onTab(key);
            }}
          >
            <TabIcon tab={key} />
            {label}
          </button>
        ))}
      </div>
    </section>
  );
}

// ------------------------------------------------------------- Messages ---

function Conversations({
  threads,
  loading,
  error,
  nameOf,
  onPick,
}: {
  threads: Thread[];
  loading: boolean;
  error: string | null;
  nameOf: (thread: Thread) => string;
  onPick: (thread: Thread) => void;
}) {
  return (
    <>
      <ErrorNote error={error} />
      {loading ? <div className="msg-empty">Loading…</div> : null}
      {!loading && !error && threads.length === 0 ? (
        <div className="msg-empty">
          No texts yet. Find a customer above to start one.
        </div>
      ) : null}
      {threads.length ? (
        <div className="list" role="list" aria-label="Conversations">
          {threads.map((thread) => (
            <button
              key={thread.key}
              type="button"
              role="listitem"
              className="msg-row"
              onClick={() => onPick(thread)}
            >
              <span className="msg-top">
                {thread.unanswered > 0 ? (
                  <span className="msg-dot" aria-label="Unanswered" />
                ) : null}
                <span className="msg-name">{nameOf(thread)}</span>
                <span className="msg-wait">
                  {fmtDateTime(thread.latest.sent_at)}
                </span>
              </span>
              <span className="msg-text">{threadPreview(thread.latest)}</span>
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

function Conversation({
  opened,
  name,
  job,
  line,
  callLine,
  onLine,
  onBack,
  onLeave,
}: {
  opened: Opened;
  name: string;
  job: Job | null;
  line: string | null;
  callLine: string | null;
  onLine: (line: string) => void;
  onBack: () => void;
  onLeave: () => void;
}) {
  const go = (to: string) => {
    onLeave();
    navigate(to);
  };
  return (
    <section className="inbox-convo" aria-label={`Conversation with ${name}`}>
      <div className="inbox-head">
        <button
          type="button"
          className="phone-back"
          aria-label="Back to Messages"
          onClick={onBack}
        >
          ‹
        </button>
        <div className="phone-who">
          <div className="msg-name">{name}</div>
          <div className="na-hint">
            {job
              ? `#${job.job_number} · ${jobAddress(job)}`
              : "Not on a job yet"}
          </div>
        </div>
        <div className="inbox-acts">
          {job ? (
            <CallButton
              number={opened.phone ? `+1${opened.phone}` : null}
              leadSource={job.lead_source}
              jobId={job.id}
              contactId={job.contact_id}
              line={callLine}
              compact
            />
          ) : null}
          {job ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => go(jobLink(job.id, "Texts"))}
            >
              Open job →
            </button>
          ) : opened.contact_id ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => go(`contact=${opened.contact_id}`)}
            >
              Open contact →
            </button>
          ) : null}
        </div>
      </div>
      {job ? (
        <TextsPanel job={job} line={line} onLine={onLine} />
      ) : (
        <div className="inbox-unmatched">
          <p className="na-hint">
            {opened.contact_id
              ? "This customer has no job yet, so there is nowhere to reply from here. Open the contact and add one."
              : "Nobody in Roof Board has this number. It is in New Leads on the pipeline until somebody makes it a lead."}
          </p>
          {opened.latest ? (
            <div className="msg-text">{threadPreview(opened.latest)}</div>
          ) : null}
        </div>
      )}
    </section>
  );
}

// --------------------------------------------------------------- the icons ---

function TabIcon({ tab }: { tab: PhoneTab }) {
  const common = {
    viewBox: "0 0 24 24",
    width: 22,
    height: 22,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (tab === "messages")
    return (
      <svg {...common}>
        <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.6-4.6A8 8 0 1 1 21 12z" />
      </svg>
    );
  if (tab === "recents")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );
  return (
    <svg {...common}>
      <circle cx="6" cy="14" r="3.5" />
      <circle cx="18" cy="14" r="3.5" />
      <path d="M6 17.5h12" />
    </svg>
  );
}
